import {
  rememberAsyncFilterLabels,
  resolveFromLabelCache,
} from '#/modules/commonComponents/table/asyncFilterLabelCache';
import {
  createAsyncMultiFilterMeta,
  createAsyncSingleFilterMeta,
  createFixedMultiFilterMeta,
  createQtyRangeFilterMeta,
  createTextFilterMeta,
  createTreeMultiFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import {
  formatBaseQty,
  formatInvStatusLabel,
  formatInventoryStockStatusLabel,
  INVENTORY_STOCK_INV_STATUS_FILTER_OPTIONS,
  INVENTORY_STOCK_STATUS_FILTER_OPTIONS,
  resolveInventoryStockMaterial,
} from '#/modules/imsMgmt/inventoryStock/helper/inventoryStockFilterOptions';
import { searchWarehouseFilterOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import {
  searchWarehouseLocationFilterOptions,
} from '#/modules/imsMgmt/settings/storePlace/helper/warehouseLocationSelectOptions';

export function buildInventoryStockColumns({
  filters = {},
  onFilterChange,
  onQtyFiltersChange,
  warehouseLabelCache,
  locationLabelCache,
}) {
  return [
    {
      header: '庫存編號',
      accessorKey: 'stockNo',
      size: 140,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'stockNo',
        label: '庫存編號',
        filterValue: filters.stockNo,
        onChange: (value) => onFilterChange('stockNo', value),
        placeholder: '搜尋庫存編號',
      }),
    },
    {
      id: 'mtrNo',
      header: '料號',
      accessorFn: (row) => resolveInventoryStockMaterial(row)?.mtrNo ?? '-',
      size: 180,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'mtrNo',
          label: '料號',
          filterValue: filters.mtrNo,
          onChange: (value) => onFilterChange('mtrNo', value),
          placeholder: '搜尋料號',
        }),
      },
    },
    {
      id: 'mtrName',
      header: '名稱',
      accessorFn: (row) => resolveInventoryStockMaterial(row)?.name ?? '-',
      size: 250,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'name',
          label: '名稱',
          filterValue: filters.name,
          onChange: (value) => onFilterChange('name', value),
          placeholder: '搜尋名稱',
        }),
      },
    },
    {
      id: 'mtrStructMain',
      header: '主分類',
      accessorFn: (row) => resolveInventoryStockMaterial(row)?.mtrStructMain?.name ?? '-',
      size: 120,
      enableSorting: false,
      enableColumnFilter: true,
      meta: createTreeMultiFilterMeta({
        key: 'mtrStructMainId',
        label: '主分類',
        treeRole: 'main',
        filterValue: filters.mtrStructMainId,
        onChange: (value) => onFilterChange('mtrStructMainId', value),
      }),
    },
    {
      id: 'mtrStructSub',
      header: '次分類',
      accessorFn: (row) => resolveInventoryStockMaterial(row)?.mtrStructSub?.name ?? '-',
      size: 120,
      enableSorting: false,
      enableColumnFilter: true,
      meta: createTreeMultiFilterMeta({
        key: 'mtrStructSubId',
        label: '次分類',
        treeRole: 'sub',
        filterValue: filters.mtrStructSubId,
        onChange: (value) => onFilterChange('mtrStructSubId', value),
      }),
    },
    {
      id: 'warehouseId',
      header: '倉別',
      accessorFn: (row) => row.warehouse?.name || '-',
      size: 140,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createAsyncMultiFilterMeta({
          key: 'warehouseId',
          label: '倉別',
          searchOptions: searchWarehouseFilterOptions,
          filterValue: filters.warehouseId,
          onChange: (value) => onFilterChange('warehouseId', value),
          resolveFilterDisplay: (values) => (
            resolveFromLabelCache(warehouseLabelCache, values)
          ),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(warehouseLabelCache, options, values);
          },
        }),
      },
    },
    {
      id: 'locationId',
      header: '儲位',
      accessorFn: (row) => row.warehouse?.location?.name || '-',
      size: 140,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        dependsOnFilters: { warehouseId: filters.warehouseId },
        ...createAsyncSingleFilterMeta({
          key: 'locationId',
          label: '儲位',
          searchOptions: searchWarehouseLocationFilterOptions,
          filterValue: Array.isArray(filters.locationId)
            ? filters.locationId[0]
            : filters.locationId,
          onChange: (value) => onFilterChange('locationId', value),
          resolveFilterDisplay: (values) => (
            resolveFromLabelCache(locationLabelCache, values)
          ),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(locationLabelCache, options, values);
          },
        }),
      },
    },
    {
      id: 'invStatus',
      header: '庫存狀態',
      accessorFn: (row) => formatInvStatusLabel(row.invStatus ?? row.inventoryStatus),
      size: 112,
      enableColumnFilter: true,
      meta: createFixedMultiFilterMeta({
        key: 'invStatus',
        label: '庫存狀態',
        options: INVENTORY_STOCK_INV_STATUS_FILTER_OPTIONS,
        filterValue: filters.invStatus,
        onChange: (value) => onFilterChange('invStatus', value),
      }),
    },
    {
      id: 'status',
      header: '狀態',
      accessorFn: (row) => formatInventoryStockStatusLabel(row.status),
      size: 96,
      enableColumnFilter: true,
      meta: createFixedMultiFilterMeta({
        key: 'status',
        label: '狀態',
        options: INVENTORY_STOCK_STATUS_FILTER_OPTIONS,
        filterValue: filters.status,
        onChange: (value) => onFilterChange('status', value),
      }),
    },
    {
      id: 'baseQty',
      header: '數量',
      accessorFn: (row) => formatBaseQty(row),
      size: 140,
      enableSorting: false,
      enableColumnFilter: true,
      meta: createQtyRangeFilterMeta({
        label: '數量',
        qtyGt: filters.baseQtyGt,
        qtyMin: filters.baseQtyMin,
        qtyMax: filters.baseQtyMax,
        onChange: onQtyFiltersChange,
      }),
    },
    {
      header: '備註',
      accessorKey: 'remark',
      size: 160,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'remark',
          label: '備註',
          filterValue: filters.remark,
          onChange: (value) => onFilterChange('remark', value),
          placeholder: '搜尋備註',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={row.original?.remark || '—'} />
      ),
    },
  ];
}
